import {
  Cause,
  type Context,
  Effect,
  Exit,
  Option,
  Predicate,
  Record,
} from 'effect';

type TaggedError<E> = Extract<E, { readonly _tag: string }>;

export type ThrowOnTag<E> = {
  readonly [Tag in TaggedError<E>['_tag']]?: (
    error: Extract<E, { readonly _tag: Tag }>,
  ) => unknown;
};

type MethodError<M> = M extends (
  ...args: ReadonlyArray<never>
) => Effect.Effect<unknown, infer E, unknown>
  ? E
  : never;

/** Every endpoint error of a generated `HttpApiClient` (groups of methods). */
type ClientError<Client> = {
  [Group in keyof Client]: {
    [Method in keyof Client[Group]]: MethodError<Client[Group][Method]>;
  }[keyof Client[Group]];
}[keyof Client];

interface ClientRuntime<Id> {
  readonly runPromiseExit: <A, E>(
    effect: Effect.Effect<A, E, Id>,
    options?: { readonly signal?: AbortSignal | undefined },
  ) => Promise<Exit.Exit<A, unknown>>;
}

/**
 * Resolves a loader-side exit: the success value, or a throw. A tagged failure
 * listed in `throwOnTag` is replaced by what its handler returns, because
 * TanStack Router only recognises `notFound()` and `redirect()` when a loader
 * throws them; any other failure throws the squashed cause.
 */
export const valueOrThrow = <A, E>(
  exit: Exit.Exit<A, E>,
  throwOnTag?: ThrowOnTag<E>,
) => {
  if (Exit.isSuccess(exit)) return exit.value;
  const handlers: Record.ReadonlyRecord<
    string,
    ((error: never) => unknown) | undefined
  > = { ...throwOnTag };
  const handled = Cause.findErrorOption(exit.cause).pipe(
    Option.flatMap((error) =>
      Record.findFirst(
        handlers,
        (handler, tag) =>
          handler !== undefined && Predicate.isTagged(error, tag),
      ).pipe(
        // The matched tag makes `error` that handler's own error type.
        Option.map(([, handler]) => handler?.(error as never)),
      ),
    ),
  );
  throw Option.getOrElse(handled, () => Cause.squash(exit.cause));
};

/**
 * Builds the loader-side caller for a typed API client: it runs the call on
 * whichever runtime `getRuntime` returns (server or browser) and resolves it
 * with `valueOrThrow`. Per-call handlers win over the defaults.
 */
export const makeCallApiPromise =
  <Id, Client>(
    client: Context.Key<Id, Client>,
    getRuntime: () => ClientRuntime<Id>,
    defaults?: { readonly throwOnTag?: ThrowOnTag<ClientError<Client>> },
  ) =>
  <A, E>(
    call: (client: Client) => Effect.Effect<A, E>,
    options?: {
      readonly throwOnTag?: ThrowOnTag<NoInfer<E>>;
      readonly signal?: AbortSignal;
    },
  ) =>
    getRuntime()
      .runPromiseExit(Effect.flatMap(client, call), {
        signal: options?.signal,
      })
      .then((exit) =>
        valueOrThrow(exit, { ...defaults?.throwOnTag, ...options?.throwOnTag }),
      );
